import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Trash2, Plus, MapPin, ShoppingBag, ShieldCheck, LogOut, Download, FileText } from "lucide-react";
import { api } from "../lib/api";
import { INDIAN_STATES } from "../lib/india";
import { toast } from "sonner";
import { useSEO } from "../lib/useSEO";

export default function Account() {
  useSEO({ title: "My Account | Joy Pearl", description: "Manage your Joy Pearl account, saved addresses, seller application, and DPDP privacy controls.", noIndex: true });
  const { user, loading, refreshMe, logout } = useAuth();
  const navigate = useNavigate();
  const [adding, setAdding] = useState(false);
  const [newAddr, setNewAddr] = useState({ name: "", phone: "", line1: "", line2: "", city: "", state: "", pincode: "", landmark: "", is_default: false });
  const [applying, setApplying] = useState(false);
  const [biz, setBiz] = useState({ business_name: "", gstin: "", description: "" });
  const [consent, setConsent] = useState({ terms: true, marketing: false, analytics: false });
  const [dpdpRequests, setDpdpRequests] = useState([]);
  const [dpdpForm, setDpdpForm] = useState({ request_type: "access", note: "" });

  useEffect(() => {
    if (!user) return;
    api.get("/dpdp/consent").then((r) => setConsent({ terms: r.data.terms ?? true, marketing: !!r.data.marketing, analytics: !!r.data.analytics })).catch(() => {});
    api.get("/dpdp/requests/mine").then((r) => setDpdpRequests(r.data)).catch(() => {});
  }, [user]);

  if (loading) return <div className="jp-container py-20 text-center text-brand-muted">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;

  const submitAddress = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/addresses", newAddr); await refreshMe(); setAdding(false); setNewAddr({ name: "", phone: "", line1: "", line2: "", city: "", state: "", pincode: "", landmark: "", is_default: false }); toast.success("Address added"); }
    catch { toast.error("Could not save"); }
  };
  const deleteAddress = async (id) => {
    try { await api.delete(`/auth/addresses/${id}`); await refreshMe(); toast.success("Removed"); }
    catch { toast.error("Could not remove"); }
  };
  const applySeller = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/seller/apply", biz); await refreshMe(); toast.success("Seller application submitted — awaiting admin approval"); setApplying(false); }
    catch { toast.error("Could not submit"); }
  };

  const updateConsent = async (key, val) => {
    const next = { ...consent, [key]: val };
    setConsent(next);
    try { await api.post("/dpdp/consent", next); toast.success("Preferences saved"); }
    catch { toast.error("Could not save"); }
  };

  const downloadMyData = async () => {
    try {
      const { data } = await api.get("/dpdp/my-data");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `joypearl-mydata-${Date.now()}.json`;
      a.click();
      toast.success("Data export downloaded");
    } catch { toast.error("Export failed"); }
  };

  const submitDpdp = async (e) => {
    e.preventDefault();
    try {
      await api.post("/dpdp/requests", dpdpForm);
      const { data } = await api.get("/dpdp/requests/mine");
      setDpdpRequests(data);
      setDpdpForm({ request_type: "access", note: "" });
      toast.success("Request submitted. We respond within 30 days under DPDP Act.");
    } catch { toast.error("Could not submit request"); }
  };

  return (
    <div className="jp-container py-8 md:py-12 max-w-4xl" data-testid="account-page">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
        <div>
          <p className="jp-eyebrow mb-2">Account</p>
          <h1 className="font-serif text-3xl md:text-5xl">Hello, {user.name.split(" ")[0]}.</h1>
          <p className="text-brand-muted text-sm mt-2">{user.email} • Role: <span className="capitalize">{user.role}</span>{user.role === "seller" && user.seller_status && <> • <span className="capitalize">{user.seller_status}</span></>}</p>
        </div>
        <button onClick={() => { logout(); navigate("/"); }} className="jp-btn-outline" data-testid="logout-btn"><LogOut size={14} className="mr-2"/>Sign Out</button>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mb-10">
        <Link to="/orders" className="bg-white border border-brand-line p-5 hover:border-brand-primary transition-colors flex items-center gap-3" data-testid="account-orders"><ShoppingBag size={20} className="text-brand-primary"/> My Orders</Link>
        <Link to="/wishlist" className="bg-white border border-brand-line p-5 hover:border-brand-primary transition-colors flex items-center gap-3" data-testid="account-wishlist"><MapPin size={20} className="text-brand-primary"/> Wishlist</Link>
        {user.role === "seller" && <Link to="/seller" className="bg-white border border-brand-line p-5 hover:border-brand-primary transition-colors flex items-center gap-3" data-testid="account-seller"><ShieldCheck size={20} className="text-brand-primary"/> Seller Dashboard</Link>}
        {user.role === "admin" && <Link to="/admin" className="bg-white border border-brand-line p-5 hover:border-brand-primary transition-colors flex items-center gap-3" data-testid="account-admin"><ShieldCheck size={20} className="text-brand-primary"/> Admin</Link>}
      </div>

      <section className="bg-white border border-brand-line p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-xl">Saved Addresses</h2>
          {!adding && <button onClick={() => setAdding(true)} className="jp-btn-outline px-3 py-2 text-xs" data-testid="add-addr-btn"><Plus size={14} className="mr-1"/>Add</button>}
        </div>
        {!user.addresses?.length && !adding && <p className="text-sm text-brand-muted">No addresses saved yet.</p>}
        <div className="space-y-3">
          {user.addresses?.map((a) => (
            <div key={a.id} className="border border-brand-line p-4 flex items-start justify-between gap-3" data-testid={`account-address-${a.id}`}>
              <div>
                <p className="font-medium">{a.name} {a.is_default && <span className="jp-badge ml-2">Default</span>}</p>
                <p className="text-sm text-brand-muted">{a.phone}</p>
                <p className="text-sm text-brand-muted mt-1">{a.line1}{a.line2 ? `, ${a.line2}` : ""}, {a.city}, {a.state} {a.pincode}</p>
              </div>
              <button onClick={() => deleteAddress(a.id)} className="text-brand-muted hover:text-red-700" aria-label="Delete"><Trash2 size={16}/></button>
            </div>
          ))}
        </div>
        {adding && (
          <form onSubmit={submitAddress} className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input required placeholder="Full name" className="jp-input" value={newAddr.name} onChange={(e) => setNewAddr({...newAddr, name: e.target.value})}/>
            <input required placeholder="Phone" pattern="\d{10}" className="jp-input" value={newAddr.phone} onChange={(e) => setNewAddr({...newAddr, phone: e.target.value})}/>
            <input required placeholder="Address line 1" className="jp-input sm:col-span-2" value={newAddr.line1} onChange={(e) => setNewAddr({...newAddr, line1: e.target.value})}/>
            <input placeholder="Address line 2" className="jp-input sm:col-span-2" value={newAddr.line2} onChange={(e) => setNewAddr({...newAddr, line2: e.target.value})}/>
            <input required placeholder="City" className="jp-input" value={newAddr.city} onChange={(e) => setNewAddr({...newAddr, city: e.target.value})}/>
            <select required className="jp-input" value={newAddr.state} onChange={(e) => setNewAddr({...newAddr, state: e.target.value})}>
              <option value="">Select State / UT…</option>
              {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <input required placeholder="Pincode" pattern="\d{6}" className="jp-input" value={newAddr.pincode} onChange={(e) => setNewAddr({...newAddr, pincode: e.target.value})}/>
            <input placeholder="Landmark" className="jp-input" value={newAddr.landmark} onChange={(e) => setNewAddr({...newAddr, landmark: e.target.value})}/>
            <label className="text-sm flex items-center gap-2 sm:col-span-2"><input type="checkbox" checked={newAddr.is_default} onChange={(e) => setNewAddr({...newAddr, is_default: e.target.checked})}/> Set as default</label>
            <div className="sm:col-span-2 flex gap-3">
              <button type="submit" className="jp-btn-primary">Save</button>
              <button type="button" onClick={() => setAdding(false)} className="jp-btn-outline">Cancel</button>
            </div>
          </form>
        )}
      </section>

      {user.role === "customer" && (
        <section className="bg-white border border-brand-line p-6 mb-8">
          <h2 className="font-serif text-xl mb-2">Sell on Joy Pearl</h2>
          <p className="text-sm text-brand-muted mb-4">Have a small brand? Apply to become a seller. Approval typically takes 1-2 business days.</p>
          {!applying ? (
            <button onClick={() => setApplying(true)} className="jp-btn-outline" data-testid="apply-seller-btn">Apply as Seller</button>
          ) : (
            <form onSubmit={applySeller} className="space-y-4">
              <input required placeholder="Business / Brand name" className="jp-input" value={biz.business_name} onChange={(e) => setBiz({...biz, business_name: e.target.value})}/>
              <input placeholder="GSTIN (optional)" className="jp-input" value={biz.gstin} onChange={(e) => setBiz({...biz, gstin: e.target.value})}/>
              <textarea placeholder="About your brand" className="jp-input min-h-[80px]" value={biz.description} onChange={(e) => setBiz({...biz, description: e.target.value})}/>
              <div className="flex gap-3">
                <button type="submit" className="jp-btn-primary">Submit Application</button>
                <button type="button" onClick={() => setApplying(false)} className="jp-btn-outline">Cancel</button>
              </div>
            </form>
          )}
        </section>
      )}

      <section className="bg-white border border-brand-line p-6 mb-8" data-testid="dpdp-section">
        <div className="flex items-center gap-3 mb-2"><ShieldCheck size={18} className="text-brand-primary"/><h2 className="font-serif text-xl">Privacy & Your Data (DPDP Act)</h2></div>
        <p className="text-sm text-brand-muted mb-4">Under India's DPDP Act 2023, you have the right to access, correct, and erase your personal data. Manage your consent and requests below.</p>

        <div className="grid sm:grid-cols-2 gap-3 mt-4">
          <label className="flex items-start gap-3 border border-brand-line p-3 cursor-pointer">
            <input type="checkbox" checked={consent.marketing} onChange={(e) => updateConsent("marketing", e.target.checked)} data-testid="consent-marketing"/>
            <span className="text-sm"><strong className="block">Marketing communications</strong><span className="text-brand-muted">Receive offers, new arrivals & festive launches.</span></span>
          </label>
          <label className="flex items-start gap-3 border border-brand-line p-3 cursor-pointer">
            <input type="checkbox" checked={consent.analytics} onChange={(e) => updateConsent("analytics", e.target.checked)} data-testid="consent-analytics"/>
            <span className="text-sm"><strong className="block">Site analytics</strong><span className="text-brand-muted">Anonymous usage data to improve the store.</span></span>
          </label>
        </div>

        <div className="mt-6 grid sm:grid-cols-2 gap-3">
          <button onClick={downloadMyData} className="jp-btn-outline w-full justify-center" data-testid="download-mydata">
            <Download size={14} className="mr-2"/> Download my data
          </button>
          <Link to="/legal/privacy" className="jp-btn-outline w-full justify-center">
            <FileText size={14} className="mr-2"/> Privacy Policy & Grievance
          </Link>
        </div>

        <form onSubmit={submitDpdp} className="mt-6 border-t border-brand-line pt-5 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="dpdp-request-form">
          <select className="jp-input" value={dpdpForm.request_type} onChange={(e) => setDpdpForm({...dpdpForm, request_type: e.target.value})}>
            <option value="access">Request data access</option>
            <option value="correction">Request correction</option>
            <option value="erasure">Request account erasure</option>
            <option value="grievance">Raise a grievance</option>
          </select>
          <input placeholder="Add a note (optional)" className="jp-input" value={dpdpForm.note} onChange={(e) => setDpdpForm({...dpdpForm, note: e.target.value})}/>
          <button type="submit" className="jp-btn-primary sm:col-span-2" data-testid="dpdp-submit">Submit Request</button>
        </form>

        {dpdpRequests.length > 0 && (
          <div className="mt-6 border-t border-brand-line pt-5">
            <p className="jp-eyebrow mb-3">Your requests</p>
            <ul className="space-y-2 text-sm">
              {dpdpRequests.map((r) => (
                <li key={r.id} className="flex items-center justify-between border border-brand-line p-3" data-testid={`dpdp-req-${r.id}`}>
                  <span className="capitalize">{r.request_type}</span>
                  <span className="jp-badge capitalize">{r.status.replace("_", " ")}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>
    </div>
  );
}
